import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * In-app notification inbox, ported from the Flutter InboxService.
 * Stored at users/{uid}/notifications/{id}. Distinct from FCM push.
 */
const notifsRef = (uid) => collection(db, 'users', uid, 'notifications');

export function watchNotifications(uid, callback, { max = 50, onError } = {}) {
  const q = query(notifsRef(uid), orderBy('createdAt', 'desc'), limit(max));
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError,
  );
}

/** Unread count for the header badge (capped so the query stays cheap). */
export function watchUnreadCount(uid, callback, { cap = 50, onError } = {}) {
  const q = query(notifsRef(uid), where('read', '==', false), limit(cap));
  return onSnapshot(q, (snap) => callback(snap.size), onError);
}

export async function markAllRead(uid) {
  const unread = await getDocs(
    query(notifsRef(uid), where('read', '==', false), limit(400)),
  );
  if (unread.empty) return;
  const batch = writeBatch(db);
  unread.docs.forEach((d) => batch.update(d.ref, { read: true }));
  await batch.commit();
}

export function markRead(uid, id) {
  return setDoc(
    doc(db, 'users', uid, 'notifications', id),
    { read: true },
    { merge: true },
  );
}

/**
 * Writes a notification into the recipient's inbox. Actor name/avatar are
 * denormalized (one lookup). No-ops for self-actions; best-effort — callers
 * should treat failures as non-fatal so they never break the primary action.
 */
export async function createNotification({
  recipientUid,
  actorUid,
  type,
  postId = '',
  commentId = '',
  preview = '',
}) {
  if (!recipientUid || !actorUid || recipientUid === actorUid) return;
  const actor = (await getDoc(doc(db, 'users', actorUid))).data() || {};
  await addDoc(notifsRef(recipientUid), {
    type,
    actorUid,
    actorName: actor.displayName || 'Someone',
    actorAvatarUrl: actor.photoURL || '',
    postId,
    commentId,
    preview: preview.length > 140 ? preview.slice(0, 140) : preview,
    read: false,
    createdAt: serverTimestamp(),
  });
}
